/** Shared PostgreSQL connection helpers for reporting data sources (Npgsql ADO.NET format). */

export const REPORTING_SSL_OPTIONS = [
    { value: 'disable', label: 'Disable' },
    { value: 'allow', label: 'Allow' },
    { value: 'prefer', label: 'Prefer' },
    { value: 'require', label: 'Require' },
    { value: 'verify-ca', label: 'Verify CA' },
    { value: 'verify-full', label: 'Verify Full' },
];

/** First enabled data source id, or first listed. */
export function pickDefaultDataSourceId(dataSources = []) {
    if (!dataSources.length) return null;
    const row = dataSources.find(d => d.kind === 'default' && d.enabled !== false)
        ?? dataSources.find(d => d.name === 'Tenant Database' && d.enabled !== false)
        ?? dataSources.find(d => d.enabled !== false)
        ?? dataSources[0];
    return row.id ?? row.Id ?? null;
}

/** Data Vault external source (factory default name or first external). */
export function pickDataVaultDataSourceId(dataSources = []) {
    if (!dataSources.length) return null;
    const row = dataSources.find(d => d.name === 'Data Vault' && d.kind === 'external' && d.enabled !== false)
        ?? dataSources.find(d => d.kind === 'external' && d.enabled !== false);
    return row?.id ?? row?.Id ?? null;
}

/** OMOP CDM external source. */
export function pickOmopDataSourceId(dataSources = []) {
    if (!dataSources.length) return null;
    const row = dataSources.find(d => d.name === 'OMOP CDM' && d.kind === 'external' && d.enabled !== false)
        ?? dataSources.find(d => d.kind === 'external' && d.name?.toLowerCase().includes('omop') && d.enabled !== false);
    return row?.id ?? row?.Id ?? null;
}

export function buildNpgsqlConnectionString({ host, port, database, username, password, sslMode }) {
    const parts = [
        `Host=${host || 'localhost'}`,
        `Port=${port || 5432}`,
        `Database=${database || 'postgres'}`,
    ];
    if (username) parts.push(`Username=${username}`);
    if (password) parts.push(`Password=${password}`);
    if (sslMode && sslMode !== 'prefer') {
        const sslLabel = sslMode === 'verify-ca' ? 'Verify CA'
            : sslMode === 'verify-full' ? 'Verify Full'
                : sslMode.charAt(0).toUpperCase() + sslMode.slice(1);
        parts.push(`SSL Mode=${sslLabel}`);
    }
    return `${parts.join('; ')};`;
}

function parseUri(uri) {
    if (!uri) return {};
    try {
        const url = new URL(uri.replace(/^postgres:\/\//, 'postgresql://'));
        return {
            host: url.hostname || 'localhost',
            port: url.port ? parseInt(url.port, 10) : 5432,
            database: url.pathname.replace(/^\//, ''),
            username: url.username ? decodeURIComponent(url.username) : '',
            password: url.password ? decodeURIComponent(url.password) : '',
            sslMode: url.searchParams.get('sslmode') || 'prefer',
        };
    } catch {
        return {};
    }
}

/** Parse Npgsql ADO.NET or postgresql:// connection strings into form fields. */
export function parseConnectionString(cs) {
    if (!cs) return {};
    if (/^postgres(ql)?:\/\//i.test(cs)) return parseUri(cs);

    const map = {};
    cs.split(';').forEach(part => {
        const eq = part.indexOf('=');
        if (eq < 0) return;
        map[part.slice(0, eq).trim().toLowerCase()] = part.slice(eq + 1).trim();
    });
    const sslRaw = (map['ssl mode'] || 'prefer').toLowerCase();
    return {
        host: map.host || 'localhost',
        port: map.port ? parseInt(map.port, 10) : 5432,
        database: map.database || '',
        username: map.username || map.user || '',
        password: map.password || '',
        sslMode: sslRaw.replace(/\s+/g, '-'),
    };
}

export function connectionFieldsFromDataSource(ds) {
    const base = {
        name: ds?.name ?? '',
        description: ds?.description ?? '',
        enabled: ds?.enabled !== false,
        sslMode: 'prefer',
        port: 5432,
        host: 'localhost',
    };
    if (ds?.connectionString) {
        return { ...base, ...parseConnectionString(ds.connectionString) };
    }
    return base;
}

export function buildConnectionStringFromForm(values, existingConnectionString) {
    const existing = existingConnectionString ? parseConnectionString(existingConnectionString) : {};
    return buildNpgsqlConnectionString({
        host: values.host,
        port: values.port,
        database: values.database,
        username: values.username,
        password: values.password || existing.password || '',
        sslMode: values.sslMode,
    });
}
